import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useMutation, useApi } from '../hooks/useApi';
import { Settings as SettingsIcon, Upload, Trash2, Save, RefreshCw, Store, Image, ToggleRight, Shield, Clock, HardDrive, Globe, AlertTriangle, CheckCircle2, Database, Mail, ShoppingCart, Server, Play, CheckCircle, XCircle, Loader, Languages } from 'lucide-react';
import env from '../config/env';
import { notificationService } from '../utils/notifications';

function Settings() {
  const { isPrincipal } = useAuth();
  const { updateSettings: updateGlobalSettings, refreshSettings: refreshGlobalSettings } = useSettings();
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const initializedRef = useRef(false);

  const { data: settingsData, execute: refetchSettings, loading: loadingSettings } = useApi(
    () => apiService.get('/settings'),
    [],
    { immediate: true, showNotifications: false }
  );

  const settings = settingsData || {};

  const [form, setForm] = useState({
    emri_duqanit: '',
    furnizuesit_aktiv: true,
    monedha: 'Denar',
    monedha_kodi: 'DEN',
    formati_data: 'd/m/Y',
    formati_ore: 'H:i',
    session_timeout_minuta: 60,
    max_upload_size_mb: 5,
    maintenance_mode: false,
    maintenance_message: '',
    // .env variables
    app_url: '',
    db_connection: '',
    db_host: '',
    db_port: '',
    db_database: '',
    db_username: '',
    db_password: '',
    cache_driver: '',
    session_driver: '',
    session_lifetime: null,
    mail_mailer: '',
    mail_host: '',
    mail_port: '',
    mail_username: '',
    mail_password: '',
    mail_encryption: '',
    mail_from_address: '',
    mail_from_name: '',
    wc_api_url: '',
    wc_consumer_key: '',
    wc_consumer_secret: '',
    sync_webhook_secret: '',
    sync_allow_unsigned_webhooks: false,
    sync_default_lokacion_id: null,
    sync_system_user_id: null,
    sync_system_username: '',
    sync_push_woo: false,
    wp_table_prefix: '',
    wp_trp_dictionary_table: '',
    wp_trp_original_strings_table: '',
    wp_db_host: '',
    wp_db_port: '',
    wp_db_database: '',
    wp_db_username: '',
    wp_db_password: '',
  });
  
  const [activeEnvTab, setActiveEnvTab] = useState('app');

  useEffect(() => {
    if (!settingsData) return;
    
    const settingsId = settingsData.id;
    const lastId = initializedRef.current;
    
    // Only update if settings ID changed or first time
    if (settingsId !== lastId) {
      initializedRef.current = settingsId;
      
      // Map currency names to currency codes
      const currencyCodeMap = {
        'Denar': 'DEN',
        'EUR': 'EUR',
        'USD': 'USD',
        'Lek': 'ALL',
      };
      
      const defaultMonedha = settingsData.monedha || 'Denar';
      const defaultMonedhaKodi = settingsData.monedha_kodi || currencyCodeMap[defaultMonedha] || 'DEN';
      
      setForm({
        emri_duqanit: settingsData.emri_duqanit || '',
        furnizuesit_aktiv: settingsData.furnizuesit_aktiv ?? true,
        monedha: defaultMonedha,
        monedha_kodi: defaultMonedhaKodi,
        formati_data: settingsData.formati_data || 'd/m/Y',
        formati_ore: settingsData.formati_ore || 'H:i',
        session_timeout_minuta: settingsData.session_timeout_minuta || 60,
        max_upload_size_mb: settingsData.max_upload_size_mb || 5,
        maintenance_mode: settingsData.maintenance_mode || false,
        maintenance_message: settingsData.maintenance_message || '',
        // .env variables
        app_url: settingsData.app_url || '',
        db_connection: settingsData.db_connection || '',
        db_host: settingsData.db_host || '',
        db_port: settingsData.db_port || '',
        db_database: settingsData.db_database || '',
        db_username: settingsData.db_username || '',
        db_password: settingsData.db_password || '',
        cache_driver: settingsData.cache_driver || '',
        session_driver: settingsData.session_driver || '',
        session_lifetime: settingsData.session_lifetime || null,
        mail_mailer: settingsData.mail_mailer || '',
        mail_host: settingsData.mail_host || '',
        mail_port: settingsData.mail_port || '',
        mail_username: settingsData.mail_username || '',
        mail_password: settingsData.mail_password || '',
        mail_encryption: settingsData.mail_encryption || '',
        mail_from_address: settingsData.mail_from_address || '',
        mail_from_name: settingsData.mail_from_name || '',
        wc_api_url: settingsData.wc_api_url || '',
        wc_consumer_key: settingsData.wc_consumer_key || '',
        wc_consumer_secret: settingsData.wc_consumer_secret || '',
        sync_webhook_secret: settingsData.sync_webhook_secret || '',
        sync_allow_unsigned_webhooks: settingsData.sync_allow_unsigned_webhooks || false,
        sync_default_lokacion_id: settingsData.sync_default_lokacion_id || null,
        sync_system_user_id: settingsData.sync_system_user_id || null,
        sync_system_username: settingsData.sync_system_username || '',
        sync_push_woo: settingsData.sync_push_woo || false,
        wp_table_prefix: settingsData.wp_table_prefix || '',
        wp_trp_dictionary_table: settingsData.wp_trp_dictionary_table || '',
        wp_trp_original_strings_table: settingsData.wp_trp_original_strings_table || '',
        wp_db_host: settingsData.wp_db_host || '',
        wp_db_port: settingsData.wp_db_port || '',
        wp_db_database: settingsData.wp_db_database || '',
        wp_db_username: settingsData.wp_db_username || '',
        wp_db_password: settingsData.wp_db_password || '',
      });

      // Set logo preview - përdor logo_url nëse ekziston, përndryshe ndërto nga logo_path
      if (settingsData.logo_url) {
        setLogoPreview(settingsData.logo_url);
      } else if (settingsData.logo_path) {
        setLogoPreview(`${env.API_URL_Image}/storage/${settingsData.logo_path}`);
      } else {
        setLogoPreview(null);
      }
    }
  }, [settingsData?.id]);

  const { mutate: updateSettings, loading: updatingSettings } = useMutation(
    async (data) => {
      const response = await apiService.put('/settings', data);
      return response;
    },
    {
      onSuccess: (response) => {
        // Update global settings context me response data direkt
        if (response?.data) {
          updateGlobalSettings(response.data);
          // Përditëso edhe form state me të dhënat e reja
          setForm(prevForm => ({
            ...prevForm,
            ...response.data
          }));
        }
        
        // Refresh global settings context për të siguruar që të gjitha komponentët marrin vlerat e reja
        // Përdorim refreshGlobalSettings që bën fetch nga serveri
        // Shto delay për të dhënë kohë serverit të përditësojë cache
        setTimeout(() => {
          refreshGlobalSettings().catch((error) => {
            // Log error por mos shfaq notifikim - settings janë ruajtur tashmë
            console.warn('Error refreshing global settings after save:', error);
          });
        }, 500); // 500ms delay për të dhënë kohë serverit të përditësojë cache
        
        notificationService.success('Cilësimet u përditësuan me sukses!');
      },
      successMessage: '',
    }
  );

  // Sync saving state me loading state nga mutation
  useEffect(() => {
    setSaving(updatingSettings);
  }, [updatingSettings]);

  const handleSave = () => {
    updateSettings(form);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      notificationService.error('Ju lutem zgjidhni një file imazh');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      notificationService.error('File duhet të jetë më i vogël se 5MB');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await apiService.post('/settings/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.logo_url || response.logo_path) {
        // Përdor logo_url nëse ekziston, përndryshe ndërto nga logo_path
        let newLogoUrl;
        if (response.logo_url) {
          // Nëse logo_url tashmë është URL e plotë, përdor direkt
          if (response.logo_url.startsWith('http://') || response.logo_url.startsWith('https://')) {
            newLogoUrl = response.logo_url;
          } else {
            // Nëse është relative path, shto base URL
            const baseUrl = env.API_URL_Image || env.API_URL || '';
            let cleanBaseUrl = baseUrl.replace(/\/api$/, '');
            // Nëse baseUrl nuk përfundon me /public, shto /public
            if (!cleanBaseUrl.endsWith('/public')) {
              cleanBaseUrl = cleanBaseUrl.replace(/\/public$/, '') + '/public';
            }
            newLogoUrl = `${cleanBaseUrl}${response.logo_url.startsWith('/') ? '' : '/'}${response.logo_url}`;
          }
        } else {
          // Ndërto nga logo_path
          const baseUrl = env.API_URL_Image || env.API_URL || '';
          let cleanBaseUrl = baseUrl.replace(/\/api$/, '');
          // Nëse baseUrl nuk përfundon me /public, shto /public
          if (!cleanBaseUrl.endsWith('/public')) {
            cleanBaseUrl = cleanBaseUrl.replace(/\/public$/, '') + '/public';
          }
          newLogoUrl = `${cleanBaseUrl}/storage/${response.logo_path}`;
        }
        setLogoPreview(newLogoUrl);
        
        // Update global settings menjëherë
        const updatedSettings = await apiService.get('/settings');
        if (updatedSettings) {
          // Shto logo_url në settings nëse nuk ekziston
          if (!updatedSettings.logo_url && updatedSettings.logo_path) {
            updatedSettings.logo_url = newLogoUrl;
          }
          updateGlobalSettings(updatedSettings);
        }
        
        // Refetch për të siguruar që gjithçka është e sinkronizuar
        refetchSettings();
        notificationService.success('Logo u ngarkua me sukses!');
      }
    } catch (error) {
      notificationService.error('Gabim gjatë ngarkimit të logos: ' + (error.message || 'Gabim i panjohur'));
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('A jeni të sigurt që dëshironi të fshini logon?')) return;

    try {
      await apiService.delete('/settings/logo');
      setLogoPreview(null);
      
      // Update global settings menjëherë
      const updatedSettings = await apiService.get('/settings');
      if (updatedSettings) {
        updatedSettings.logo_url = null;
        updatedSettings.logo_path = null;
        updateGlobalSettings(updatedSettings);
      }
      
      // Refetch për të siguruar që gjithçka është e sinkronizuar
      refetchSettings();
      notificationService.success('Logo u fshi me sukses!');
    } catch (error) {
      notificationService.error('Gabim gjatë fshirjes së logos: ' + (error.message || 'Gabim i panjohur'));
    }
  };

  const handleToggleMaintenance = async () => {
    try {
      await apiService.put('/settings/toggle-maintenance', {
        maintenance_message: form.maintenance_message,
      });
      refetchSettings();
      const newMode = !form.maintenance_mode;
      notificationService.success(
        `Maintenance mode u ${newMode ? 'aktivizua' : 'çaktivizua'} me sukses!`
      );
    } catch (error) {
      notificationService.error('Gabim: ' + (error.message || 'Gabim i panjohur'));
    }
  };

  if (!isPrincipal) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center card-premium p-8">
          <Shield className="mx-auto mb-4 text-red-500" size={48} />
          <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-4">
            Qasje e Kufizuar
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Vetëm principal user ka qasje në këtë faqe.
          </p>
        </div>
      </div>
    );
  }

  if (loadingSettings) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="mx-auto mb-4 text-primary-600 dark:text-primary-400 animate-spin" size={48} />
          <p className="text-gray-600 dark:text-gray-400">Duke ngarkuar cilësimet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Premium */}
      <div className="card-premium-hover p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg">
              <SettingsIcon size={28} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">
                Cilësimet e Sistemit
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Menaxhoni konfigurimet e sistemit dhe cilësimet e përgjithshme
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2 px-6 py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
          >
            {saving ? (
              <>
                <RefreshCw size={20} className="animate-spin" />
                Po ruhet...
              </>
            ) : (
              <>
                <Save size={20} />
                Ruaj Ndryshimet
              </>
            )}
          </button>
        </div>
      </div>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="card-premium-hover p-6 animate-slide-up">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Store size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
              Cilësime të Përgjithshme
            </h2>
          </div>

          <div className="space-y-5">
            <div>
              <label className="form-label flex items-center gap-2">
                <Store size={16} className="text-gray-500 dark:text-gray-400" />
                Emri i Dyqanit
              </label>
              <input
                type="text"
                value={form.emri_duqanit}
                onChange={(e) => setForm({ ...form, emri_duqanit: e.target.value })}
                className="input-premium"
                placeholder="Shkruaj emrin e dyqanit"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Ky emër do të shfaqet në të gjithë sistemin dhe në logo
              </p>
            </div>

            <div>
              <label className="form-label flex items-center gap-2">
                <Globe size={16} className="text-gray-500 dark:text-gray-400" />
                Monedha
              </label>
              <select
                value={form.monedha}
                onChange={(e) => {
                  const selectedMonedha = e.target.value;
                  const currencyCodeMap = {
                    'Denar': 'DEN',
                    'EUR': 'EUR',
                    'USD': 'USD',
                    'Lek': 'ALL',
                  };
                  setForm({ 
                    ...form, 
                    monedha: selectedMonedha,
                    monedha_kodi: currencyCodeMap[selectedMonedha] || 'DEN'
                  });
                }}
                className="input-premium"
              >
                <option value="Denar">Denar (DEN)</option>
                <option value="EUR">Euro (EUR)</option>
                <option value="USD">Dollar (USD)</option>
                <option value="Lek">Lek (ALL)</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Formati i Datës</label>
                <input
                  type="text"
                  value={form.formati_data}
                  onChange={(e) => setForm({ ...form, formati_data: e.target.value })}
                  className="input-premium"
                  placeholder="d/m/Y"
                />
              </div>

              <div>
                <label className="form-label">Formati i Orës</label>
                <input
                  type="text"
                  value={form.formati_ore}
                  onChange={(e) => setForm({ ...form, formati_ore: e.target.value })}
                  className="input-premium"
                  placeholder="H:i"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Logo Management */}
        <div className="card-premium-hover p-6 animate-slide-up">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Image size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
              Menaxhimi i Logos
            </h2>
          </div>

          <div className="space-y-5">
            <div className="text-center">
              {logoPreview ? (
                <div className="relative inline-block group">
                  <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-xl border-2 border-gray-200 dark:border-dark-700">
                    <img
                      src={logoPreview}
                      alt={form.emri_duqanit || 'Logo'}
                      className="w-40 h-40 object-contain"
                    />
                  </div>
                  <button
                    onClick={handleDeleteLogo}
                    className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
                    title="Fshi logo"
                  >
                    <Trash2 size={18} />
                  </button>
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {form.emri_duqanit || 'Logo aktual'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-8 bg-gray-50 dark:bg-dark-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-dark-700">
                  <Image size={48} className="mx-auto mb-3 text-gray-400 dark:text-gray-500" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Nuk ka logo të ngarkuar
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Logo do të shfaqet me emrin: <strong>{form.emri_duqanit || 'Dyqani'}</strong>
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="form-label">Ngarko Logo</label>
              <label className="btn-secondary cursor-pointer flex items-center justify-center gap-2 w-full py-3 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors">
                <Upload size={18} />
                {uploadingLogo ? 'Po ngarkohet...' : 'Zgjidh File për Logo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={uploadingLogo}
                />
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                Max 5MB (JPG, PNG, SVG, WEBP) • Logo do të lidhet me emrin: <strong>{form.emri_duqanit || 'Dyqani'}</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Features Toggle */}
        <div className="card-premium-hover p-6 animate-slide-up">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <ToggleRight size={24} className="text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
              Aktivizimi i Features
            </h2>
          </div>

          <div className="space-y-5">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
              <div className="flex-1">
                <label className="form-label mb-1">Furnizuesit</label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Aktivizo/deaktivizo sistemin e furnizuesve
                </p>
              </div>
              <button
                onClick={() => setForm({ ...form, furnizuesit_aktiv: !form.furnizuesit_aktiv })}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 ${
                  form.furnizuesit_aktiv 
                    ? 'bg-green-500 dark:bg-green-600' 
                    : 'bg-gray-300 dark:bg-dark-700'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 shadow-md ${
                    form.furnizuesit_aktiv ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
              <div className="flex-1">
                <label className="form-label mb-1 flex items-center gap-2">
                  <AlertTriangle size={16} className="text-orange-500" />
                  Maintenance Mode
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Aktivizo modalitetin e mirëmbajtjes
                </p>
              </div>
              <button
                onClick={handleToggleMaintenance}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 ${
                  form.maintenance_mode 
                    ? 'bg-red-500 dark:bg-red-600' 
                    : 'bg-gray-300 dark:bg-dark-700'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 shadow-md ${
                    form.maintenance_mode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {form.maintenance_mode && (
              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800 animate-fade-in">
                <label className="form-label flex items-center gap-2">
                  <AlertTriangle size={16} className="text-orange-600 dark:text-orange-400" />
                  Mesazh për Maintenance
                </label>
                <textarea
                  value={form.maintenance_message}
                  onChange={(e) => setForm({ ...form, maintenance_message: e.target.value })}
                  className="input-premium mt-2"
                  rows="3"
                  placeholder="Shkruani mesazhin që do të shfaqet për përdoruesit..."
                />
              </div>
            )}
          </div>
        </div>

        {/* System Configuration */}
        <div className="card-premium-hover p-6 animate-slide-up">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <Shield size={24} className="text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
              Konfigurim i Sistemit
            </h2>
          </div>

          <div className="space-y-5">
            <div>
              <label className="form-label flex items-center gap-2">
                <Clock size={16} className="text-gray-500 dark:text-gray-400" />
                Session Timeout (minuta)
              </label>
              <input
                type="number"
                value={form.session_timeout_minuta}
                onChange={(e) => setForm({ ...form, session_timeout_minuta: parseInt(e.target.value) || 60 })}
                className="input-premium"
                min="5"
                max="480"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Koha pas së cilës përdoruesi do të çkyçet automatikisht
              </p>
            </div>

            <div>
              <label className="form-label flex items-center gap-2">
                <HardDrive size={16} className="text-gray-500 dark:text-gray-400" />
                Max Upload Size (MB)
              </label>
              <input
                type="number"
                value={form.max_upload_size_mb}
                onChange={(e) => setForm({ ...form, max_upload_size_mb: parseInt(e.target.value) || 5 })}
                className="input-premium"
                min="1"
                max="50"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Madhësia maksimale e file-ave që mund të ngarkohen
              </p>
            </div>

            {settings.version && (
              <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
                <label className="form-label flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-green-500" />
                  Version i Sistemit
                </label>
                <div className="mt-2">
                  <span className="inline-block px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg font-semibold">
                    v{settings.version}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* .env Variables Configuration */}
      <div className="card-premium-hover p-6 animate-slide-up mt-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Server size={24} className="text-purple-600 dark:text-purple-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
            Konfigurim .env Variables
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Ndryshoni variablat e .env file-it direkt nga këtu. Ndryshimet do të ruhen automatikisht në .env file.
        </p>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-dark-700">
          {[
            { id: 'app', label: 'App & Database', icon: <Globe size={16} /> },
            { id: 'mail', label: 'Mail', icon: <Mail size={16} /> },
            { id: 'woo', label: 'WooCommerce', icon: <ShoppingCart size={16} /> },
            { id: 'translatepress', label: 'TranslatePress', icon: <Languages size={16} /> },
            { id: 'sync', label: 'Sync', icon: <Database size={16} /> },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveEnvTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
                activeEnvTab === tab.id
                  ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* App & Database Tab */}
        {activeEnvTab === 'app' && (
          <div className="space-y-5">
            <div>
              <label className="form-label">APP_URL</label>
              <input
                type="text"
                value={form.app_url}
                onChange={(e) => setForm({ ...form, app_url: e.target.value })}
                className="input-premium"
                placeholder="http://localhost/shop/public"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">DB_CONNECTION</label>
                <input
                  type="text"
                  value={form.db_connection}
                  onChange={(e) => setForm({ ...form, db_connection: e.target.value })}
                  className="input-premium"
                  placeholder="mysql"
                />
              </div>
              <div>
                <label className="form-label">DB_HOST</label>
                <input
                  type="text"
                  value={form.db_host}
                  onChange={(e) => setForm({ ...form, db_host: e.target.value })}
                  className="input-premium"
                  placeholder="127.0.0.1"
                />
              </div>
              <div>
                <label className="form-label">DB_PORT</label>
                <input
                  type="text"
                  value={form.db_port}
                  onChange={(e) => setForm({ ...form, db_port: e.target.value })}
                  className="input-premium"
                  placeholder="3306"
                />
              </div>
              <div>
                <label className="form-label">DB_DATABASE</label>
                <input
                  type="text"
                  value={form.db_database}
                  onChange={(e) => setForm({ ...form, db_database: e.target.value })}
                  className="input-premium"
                  placeholder="hmz"
                />
              </div>
              <div>
                <label className="form-label">DB_USERNAME</label>
                <input
                  type="text"
                  value={form.db_username}
                  onChange={(e) => setForm({ ...form, db_username: e.target.value })}
                  className="input-premium"
                  placeholder="root"
                />
              </div>
              <div>
                <label className="form-label">DB_PASSWORD</label>
                <input
                  type="password"
                  value={form.db_password}
                  onChange={(e) => setForm({ ...form, db_password: e.target.value })}
                  className="input-premium"
                  placeholder=""
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">CACHE_DRIVER</label>
                <input
                  type="text"
                  value={form.cache_driver}
                  onChange={(e) => setForm({ ...form, cache_driver: e.target.value })}
                  className="input-premium"
                  placeholder="file"
                />
              </div>
              <div>
                <label className="form-label">SESSION_DRIVER</label>
                <input
                  type="text"
                  value={form.session_driver}
                  onChange={(e) => setForm({ ...form, session_driver: e.target.value })}
                  className="input-premium"
                  placeholder="file"
                />
              </div>
              <div>
                <label className="form-label">SESSION_LIFETIME</label>
                <input
                  type="number"
                  value={form.session_lifetime || ''}
                  onChange={(e) => setForm({ ...form, session_lifetime: e.target.value ? parseInt(e.target.value) : null })}
                  className="input-premium"
                  placeholder="120"
                />
              </div>
            </div>
          </div>
        )}

        {/* Mail Tab */}
        {activeEnvTab === 'mail' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">MAIL_MAILER</label>
                <input
                  type="text"
                  value={form.mail_mailer}
                  onChange={(e) => setForm({ ...form, mail_mailer: e.target.value })}
                  className="input-premium"
                  placeholder="smtp"
                />
              </div>
              <div>
                <label className="form-label">MAIL_HOST</label>
                <input
                  type="text"
                  value={form.mail_host}
                  onChange={(e) => setForm({ ...form, mail_host: e.target.value })}
                  className="input-premium"
                  placeholder="mailpit"
                />
              </div>
              <div>
                <label className="form-label">MAIL_PORT</label>
                <input
                  type="text"
                  value={form.mail_port}
                  onChange={(e) => setForm({ ...form, mail_port: e.target.value })}
                  className="input-premium"
                  placeholder="1025"
                />
              </div>
              <div>
                <label className="form-label">MAIL_USERNAME</label>
                <input
                  type="text"
                  value={form.mail_username}
                  onChange={(e) => setForm({ ...form, mail_username: e.target.value })}
                  className="input-premium"
                  placeholder=""
                />
              </div>
              <div>
                <label className="form-label">MAIL_PASSWORD</label>
                <input
                  type="password"
                  value={form.mail_password}
                  onChange={(e) => setForm({ ...form, mail_password: e.target.value })}
                  className="input-premium"
                  placeholder=""
                />
              </div>
              <div>
                <label className="form-label">MAIL_ENCRYPTION</label>
                <input
                  type="text"
                  value={form.mail_encryption}
                  onChange={(e) => setForm({ ...form, mail_encryption: e.target.value })}
                  className="input-premium"
                  placeholder="null"
                />
              </div>
              <div>
                <label className="form-label">MAIL_FROM_ADDRESS</label>
                <input
                  type="email"
                  value={form.mail_from_address}
                  onChange={(e) => setForm({ ...form, mail_from_address: e.target.value })}
                  className="input-premium"
                  placeholder="hello@example.com"
                />
              </div>
              <div>
                <label className="form-label">MAIL_FROM_NAME</label>
                <input
                  type="text"
                  value={form.mail_from_name}
                  onChange={(e) => setForm({ ...form, mail_from_name: e.target.value })}
                  className="input-premium"
                  placeholder="${APP_NAME}"
                />
              </div>
            </div>
          </div>
        )}

        {/* WooCommerce Tab */}
        {activeEnvTab === 'woo' && (
          <div className="space-y-5">
            <div>
              <label className="form-label">WC_API_URL</label>
              <input
                type="text"
                value={form.wc_api_url}
                onChange={(e) => setForm({ ...form, wc_api_url: e.target.value })}
                className="input-premium"
                placeholder="http://localhost/shop/wp-json/wc/v3/"
              />
            </div>
            <div>
              <label className="form-label">WC_CONSUMER_KEY</label>
              <input
                type="text"
                value={form.wc_consumer_key}
                onChange={(e) => setForm({ ...form, wc_consumer_key: e.target.value })}
                className="input-premium"
                placeholder="ck_..."
              />
            </div>
            <div>
              <label className="form-label">WC_CONSUMER_SECRET</label>
              <input
                type="password"
                value={form.wc_consumer_secret}
                onChange={(e) => setForm({ ...form, wc_consumer_secret: e.target.value })}
                className="input-premium"
                placeholder="cs_..."
              />
            </div>
          </div>
        )}

        {/* TranslatePress Tab */}
        {activeEnvTab === 'translatepress' && (
          <div className="space-y-5">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                <strong>Info:</strong> TranslatePress përdor bazën e të dhënave të WordPress. 
                Konfiguro lidhjen me bazën e të dhënave të WordPress dhe tabelat e TranslatePress ku ruhen përkthimet.
              </p>
            </div>
            
            {/* WordPress Database Configuration */}
            <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">WordPress Database Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">WP_DB_HOST</label>
                  <input
                    type="text"
                    value={form.wp_db_host}
                    onChange={(e) => setForm({ ...form, wp_db_host: e.target.value })}
                    className="input-premium"
                    placeholder="localhost"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Host i bazës së të dhënave të WordPress
                  </p>
                </div>
                <div>
                  <label className="form-label">WP_DB_PORT</label>
                  <input
                    type="text"
                    value={form.wp_db_port}
                    onChange={(e) => setForm({ ...form, wp_db_port: e.target.value })}
                    className="input-premium"
                    placeholder="3306"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Port i bazës së të dhënave të WordPress
                  </p>
                </div>
                <div>
                  <label className="form-label">WP_DB_DATABASE</label>
                  <input
                    type="text"
                    value={form.wp_db_database}
                    onChange={(e) => setForm({ ...form, wp_db_database: e.target.value })}
                    className="input-premium"
                    placeholder="hmzcbils_wp760"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Emri i bazës së të dhënave të WordPress
                  </p>
                </div>
                <div>
                  <label className="form-label">WP_DB_USERNAME</label>
                  <input
                    type="text"
                    value={form.wp_db_username}
                    onChange={(e) => setForm({ ...form, wp_db_username: e.target.value })}
                    className="input-premium"
                    placeholder="root"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Username për bazën e të dhënave të WordPress
                  </p>
                </div>
                <div>
                  <label className="form-label">WP_DB_PASSWORD</label>
                  <input
                    type="password"
                    value={form.wp_db_password}
                    onChange={(e) => setForm({ ...form, wp_db_password: e.target.value })}
                    className="input-premium"
                    placeholder=""
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Password për bazën e të dhënave të WordPress
                  </p>
                </div>
              </div>
            </div>

            {/* TranslatePress Table Settings */}
            <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">TranslatePress Table Settings</h3>
              <div>
                <label className="form-label">WP_TABLE_PREFIX</label>
                <input
                  type="text"
                  value={form.wp_table_prefix}
                  onChange={(e) => setForm({ ...form, wp_table_prefix: e.target.value })}
                  className="input-premium"
                  placeholder="wpya_"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Prefix i tabelave të WordPress (zakonisht wp_ ose wpya_)
                </p>
              </div>
              <div className="mt-4">
                <label className="form-label">WP_TRP_DICTIONARY_TABLE</label>
                <input
                  type="text"
                  value={form.wp_trp_dictionary_table}
                  onChange={(e) => setForm({ ...form, wp_trp_dictionary_table: e.target.value })}
                  className="input-premium"
                  placeholder="trp_dictionary_sq_mk_mk"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Emri i tabelës së TranslatePress për përkthime (shqip → maqedonisht). 
                  Format: trp_dictionary_[source_lang]_[target_lang]
                </p>
              </div>
              <div className="mt-4">
                <label className="form-label">WP_TRP_ORIGINAL_STRINGS_TABLE</label>
                <input
                  type="text"
                  value={form.wp_trp_original_strings_table}
                  onChange={(e) => setForm({ ...form, wp_trp_original_strings_table: e.target.value })}
                  className="input-premium"
                  placeholder="trp_original_strings"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Emri i tabelës së TranslatePress për stringjet origjinale. 
                  Kjo tabelë përdoret për të ruajtur stringjet origjinale para përkthimit.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Sync Tab */}
        {activeEnvTab === 'sync' && (
          <div className="space-y-5">
            <div>
              <label className="form-label">SYNC_WEBHOOK_SECRET</label>
              <input
                type="text"
                value={form.sync_webhook_secret}
                onChange={(e) => setForm({ ...form, sync_webhook_secret: e.target.value })}
                className="input-premium"
                placeholder="shop-local-secret"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="sync_allow_unsigned_webhooks"
                checked={form.sync_allow_unsigned_webhooks}
                onChange={(e) => setForm({ ...form, sync_allow_unsigned_webhooks: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="sync_allow_unsigned_webhooks" className="form-label cursor-pointer">
                SYNC_ALLOW_UNSIGNED_WEBHOOKS
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">SYNC_DEFAULT_LOKACION_ID</label>
                <input
                  type="number"
                  value={form.sync_default_lokacion_id || ''}
                  onChange={(e) => setForm({ ...form, sync_default_lokacion_id: e.target.value ? parseInt(e.target.value) : null })}
                  className="input-premium"
                  placeholder="1"
                />
              </div>
              <div>
                <label className="form-label">SYNC_SYSTEM_USER_ID</label>
                <input
                  type="number"
                  value={form.sync_system_user_id || ''}
                  onChange={(e) => setForm({ ...form, sync_system_user_id: e.target.value ? parseInt(e.target.value) : null })}
                  className="input-premium"
                  placeholder="1"
                />
              </div>
              <div>
                <label className="form-label">SYNC_SYSTEM_USERNAME</label>
                <input
                  type="text"
                  value={form.sync_system_username}
                  onChange={(e) => setForm({ ...form, sync_system_username: e.target.value })}
                  className="input-premium"
                  placeholder="sync-system"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="sync_push_woo"
                  checked={form.sync_push_woo}
                  onChange={(e) => setForm({ ...form, sync_push_woo: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="sync_push_woo" className="form-label cursor-pointer">
                  SYNC_PUSH_WOO
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Database Migrations & Seeds */}
      <div className="card-premium-hover p-6 animate-slide-up mt-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <Database size={24} className="text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
            Migracione & Seeds
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Ekzekutoni migracione dhe seeds për bazën e të dhënave. Mund të konfiguroni lidhjen me DB dhe të zgjidhni seeds që dëshironi të ekzekutoni.
        </p>

        <DatabaseManager />
      </div>
    </div>
  );
}

// Komponent për menaxhimin e database
function DatabaseManager() {
  const [dbConfig, setDbConfig] = useState({
    connection: 'mysql',
    host: '',
    port: '3306',
    database: '',
    username: '',
    password: '',
  });
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [migrations, setMigrations] = useState([]);
  const [executedMigrations, setExecutedMigrations] = useState([]);
  const [seeders, setSeeders] = useState([]);
  const [selectedSeeders, setSelectedSeeders] = useState([]);
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState('');
  const [freshMigration, setFreshMigration] = useState(false);

  // Load migrations dhe seeders
  useEffect(() => {
    loadMigrations();
    loadSeeders();
  }, []);

  const loadMigrations = async () => {
    try {
      const data = await apiService.get('/database/migrations');
      setMigrations(data.migrations || []);
      setExecutedMigrations(data.executed || []);
    } catch (error) {
      console.error('Error loading migrations:', error);
    }
  };

  const loadSeeders = async () => {
    try {
      const data = await apiService.get('/database/seeders');
      setSeeders(data.seeders || []);
    } catch (error) {
      console.error('Error loading seeders:', error);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const response = await apiService.post('/database/test-connection', {
        db_config: dbConfig,
      });
      setConnectionStatus({ success: true, message: response.message });
      notificationService.success('Lidhja me bazën e të dhënave u testua me sukses!');
    } catch (error) {
      setConnectionStatus({ success: false, message: error.response?.data?.error || error.message });
      notificationService.error('Gabim gjatë lidhjes: ' + (error.response?.data?.error || error.message));
    } finally {
      setTestingConnection(false);
    }
  };

  const runMigrations = async () => {
    setRunning(true);
    setOutput('');
    try {
      const response = await apiService.post('/database/migrate', {
        fresh: freshMigration,
        force: true,
        db_config: dbConfig,
      });
      setOutput(response.output || response.message);
      notificationService.success('Migracionet u ekzekutuan me sukses!');
      loadMigrations();
    } catch (error) {
      setOutput(error.response?.data?.error || error.message);
      notificationService.error('Gabim: ' + (error.response?.data?.error || error.message));
    } finally {
      setRunning(false);
    }
  };

  const runSeeders = async () => {
    setRunning(true);
    setOutput('');
    try {
      const response = await apiService.post('/database/seed', {
        seeders: selectedSeeders,
        db_config: dbConfig,
      });
      setOutput(response.output || response.message);
      notificationService.success('Seeds u ekzekutuan me sukses!');
    } catch (error) {
      setOutput(error.response?.data?.error || error.message);
      notificationService.error('Gabim: ' + (error.response?.data?.error || error.message));
    } finally {
      setRunning(false);
    }
  };

  const runMigrationsAndSeeders = async () => {
    setRunning(true);
    setOutput('');
    try {
      const response = await apiService.post('/database/migrate-and-seed', {
        fresh: freshMigration,
        seeders: selectedSeeders,
        db_config: dbConfig,
      });
      setOutput((response.migration_output || '') + '\n\n' + (response.seeder_output || '') + '\n\n' + response.message);
      notificationService.success('Migracionet dhe seeds u ekzekutuan me sukses!');
      loadMigrations();
    } catch (error) {
      setOutput(error.response?.data?.error || error.message);
      notificationService.error('Gabim: ' + (error.response?.data?.error || error.message));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* DB Configuration */}
      <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
        <h3 className="text-lg font-semibold mb-4">Konfigurim i Bazës së të Dhënave</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Host</label>
            <input
              type="text"
              value={dbConfig.host}
              onChange={(e) => setDbConfig({ ...dbConfig, host: e.target.value })}
              className="input-premium"
              placeholder="127.0.0.1"
            />
          </div>
          <div>
            <label className="form-label">Port</label>
            <input
              type="text"
              value={dbConfig.port}
              onChange={(e) => setDbConfig({ ...dbConfig, port: e.target.value })}
              className="input-premium"
              placeholder="3306"
            />
          </div>
          <div>
            <label className="form-label">Database</label>
            <input
              type="text"
              value={dbConfig.database}
              onChange={(e) => setDbConfig({ ...dbConfig, database: e.target.value })}
              className="input-premium"
              placeholder="hmz"
            />
          </div>
          <div>
            <label className="form-label">Username</label>
            <input
              type="text"
              value={dbConfig.username}
              onChange={(e) => setDbConfig({ ...dbConfig, username: e.target.value })}
              className="input-premium"
              placeholder="root"
            />
          </div>
          <div>
            <label className="form-label">Password</label>
            <input
              type="password"
              value={dbConfig.password}
              onChange={(e) => setDbConfig({ ...dbConfig, password: e.target.value })}
              className="input-premium"
              placeholder=""
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={testConnection}
              disabled={testingConnection}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {testingConnection ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Po teston...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Testo Lidhjen
                </>
              )}
            </button>
          </div>
        </div>
        {connectionStatus && (
          <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
            connectionStatus.success
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          }`}>
            {connectionStatus.success ? (
              <CheckCircle size={20} />
            ) : (
              <XCircle size={20} />
            )}
            <span>{connectionStatus.message}</span>
          </div>
        )}
      </div>

      {/* Migrations */}
      <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
        <h3 className="text-lg font-semibold mb-4">Migracione</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="fresh_migration"
              checked={freshMigration}
              onChange={(e) => setFreshMigration(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="fresh_migration" className="form-label cursor-pointer">
              Fresh Migration (fshin të gjitha tabelat dhe i krijon përsëri)
            </label>
          </div>
          <div className="flex gap-2">
            <button
              onClick={runMigrations}
              disabled={running}
              className="btn-primary flex items-center gap-2"
            >
              {running ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Po ekzekuton...
                </>
              ) : (
                <>
                  <Play size={16} />
                  Ekzekuto Migracione
                </>
              )}
            </button>
          </div>
          {migrations.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Migracionet ekzekutuar: {executedMigrations.length} / {migrations.length}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Seeders */}
      <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
        <h3 className="text-lg font-semibold mb-4">Seeds</h3>
        <div className="space-y-4">
          <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-dark-700 rounded-lg p-3">
            {seeders.map((seeder) => (
              <div key={seeder.name} className="flex items-center gap-2 py-2">
                <input
                  type="checkbox"
                  id={`seeder_${seeder.name}`}
                  checked={selectedSeeders.includes(seeder.class)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedSeeders([...selectedSeeders, seeder.class]);
                    } else {
                      setSelectedSeeders(selectedSeeders.filter(s => s !== seeder.class));
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor={`seeder_${seeder.name}`} className="text-sm cursor-pointer">
                  {seeder.name}
                </label>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={runSeeders}
              disabled={running || selectedSeeders.length === 0}
              className="btn-primary flex items-center gap-2"
            >
              {running ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Po ekzekuton...
                </>
              ) : (
                <>
                  <Play size={16} />
                  Ekzekuto Seeds
                </>
              )}
            </button>
            <button
              onClick={() => setSelectedSeeders(seeders.map(s => s.class))}
              className="btn-secondary"
            >
              Zgjidh të Gjitha
            </button>
            <button
              onClick={() => setSelectedSeeders([])}
              className="btn-secondary"
            >
              Hiq të Gjitha
            </button>
          </div>
        </div>
      </div>

      {/* Run Both */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h3 className="text-lg font-semibold mb-4">Ekzekuto Migracione + Seeds</h3>
        <button
          onClick={runMigrationsAndSeeders}
          disabled={running}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {running ? (
            <>
              <Loader size={16} className="animate-spin" />
              Po ekzekuton...
            </>
          ) : (
            <>
              <Play size={16} />
              Ekzekuto Migracione dhe Seeds
            </>
          )}
        </button>
      </div>

      {/* Output */}
      {output && (
        <div className="p-4 bg-gray-900 dark:bg-dark-900 rounded-lg border border-gray-700 dark:border-dark-600">
          <h3 className="text-lg font-semibold mb-2 text-white">Output</h3>
          <pre className="text-sm text-green-400 font-mono overflow-auto max-h-60 whitespace-pre-wrap">
            {output}
          </pre>
        </div>
      )}
    </div>
  );
}

export default Settings;
